import api from './apiClient';

const isPlainObject = (value) => Object.prototype.toString.call(value) === '[object Object]';

const firstNonEmptyString = (...values) => values.find(
  (value) => typeof value === 'string' && value.trim().length > 0
);

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const getEntityName = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (!isPlainObject(value)) return '';
  return firstNonEmptyString(
    value.name,
    value.company,
    value.nombre,
    value.razon_social,
    value.display_name,
    value.full_name,
    value.title
  ) || '';
};

const normalizeITPayload = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeITPayload);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const normalized = Object.fromEntries(
    Object.entries(value).map(([key, entryValue]) => [key, normalizeITPayload(entryValue)])
  );

  const entityId = firstDefined(
    normalized._id,
    normalized.id,
    normalized.uuid,
    normalized.location_id,
    normalized.asset_id,
    normalized.visit_id,
    normalized.ticket_id,
    normalized.quote_id,
    normalized.contract_id,
    normalized.payment_id,
    normalized.part_id
  );

  if (entityId !== undefined && normalized._id === undefined) {
    normalized._id = entityId;
  }

  if (normalized.id === undefined && normalized._id !== undefined) {
    normalized.id = normalized._id;
  }

  const clientId = firstDefined(
    normalized.client_id,
    normalized.client?._id,
    normalized.client?.id,
    normalized.client_info?._id,
    normalized.client_info?.id,
    normalized.cliente?._id,
    normalized.cliente?.id
  );
  if (clientId !== undefined && normalized.client_id === undefined) {
    normalized.client_id = clientId;
  }

  const locationId = firstDefined(
    normalized.location_id,
    normalized.location?._id,
    normalized.location?.id,
    normalized.location_info?._id,
    normalized.location_info?.id,
    normalized.sucursal?._id,
    normalized.sucursal?.id
  );
  if (locationId !== undefined && normalized.location_id === undefined) {
    normalized.location_id = locationId;
  }

  const clientName = firstNonEmptyString(
    normalized.client_name,
    normalized.cliente_nombre,
    normalized.company_name,
    normalized.customer_name,
    getEntityName(normalized.client),
    getEntityName(normalized.client_info),
    getEntityName(normalized.cliente)
  );
  if (clientName && !normalized.client_name) {
    normalized.client_name = clientName;
  }

  const locationName = firstNonEmptyString(
    normalized.location_name,
    normalized.sucursal_nombre,
    normalized.branch_name,
    getEntityName(normalized.location),
    getEntityName(normalized.location_info),
    getEntityName(normalized.sucursal)
  );
  if (locationName && !normalized.location_name) {
    normalized.location_name = locationName;
  }

  const employeeName = firstNonEmptyString(
    normalized.employee_name,
    normalized.user_name,
    normalized.tecnico_nombre,
    getEntityName(normalized.employee),
    getEntityName(normalized.user),
    getEntityName(normalized.tecnico)
  );
  if (employeeName && !normalized.employee_name) {
    normalized.employee_name = employeeName;
  }

  const visitNumber = firstNonEmptyString(
    normalized.visit_number,
    normalized.numero_visita,
    normalized.codigo_visita,
    normalized.visit_code,
    normalized.visit_info?.visit_number,
    normalized.visit?.visit_number
  );
  if (visitNumber && !normalized.visit_number) {
    normalized.visit_number = visitNumber;
  }

  const displayName = firstNonEmptyString(normalized.name, normalized.nombre);
  if (displayName && !normalized.name) {
    normalized.name = displayName;
  }

  const companyName = firstNonEmptyString(normalized.company, normalized.razon_social);
  if (companyName && !normalized.company) {
    normalized.company = companyName;
  }

  if (!Array.isArray(normalized.items)) {
    const listCandidate = [normalized.data, normalized.results, normalized.records].find(Array.isArray);
    if (listCandidate) {
      normalized.items = listCandidate;
    }
  }

  if (Array.isArray(normalized.items) && normalized.total === undefined) {
    normalized.total = normalized.items.length;
  }

  return normalized;
};

const normalizeITResponse = (response) => {
  const requestUrl = response?.config?.url || '';
  if (!requestUrl.includes('/it/')) {
    return response;
  }

  return {
    ...response,
    data: normalizeITPayload(response.data),
  };
};

// IT compatibility response hook. Auth/refresh handling lives in apiClient.
api.interceptors.response.use(
  (response) => normalizeITResponse(response)
);

// Dashboard
export const getDashboardSummary = () => api.get('/dashboard/summary');
export const getEmployeeStats = () => api.get('/dashboard/employee-stats');
export const getProjectStats = (projectId) => api.get(`/dashboard/project-stats/${projectId}`);
export const getFinancialSummary = () => api.get('/dashboard/financial-summary');

// Employees
export const getEmployees = (params) => api.get('/employees', { params });
export const getEmployee = (id) => api.get(`/employees/${id}`);
export const createEmployee = (data) => api.post('/employees', data);
export const updateEmployee = (id, data) => api.put(`/employees/${id}`, data);
export const deleteEmployee = (id) => api.delete(`/employees/${id}`);
export const restoreEmployee = (id) => api.post(`/employees/${id}/restore`);
export const updateEmployeeStatus = (id, status, reason) => api.put(`/employees/${id}/status`, null, { params: { status, reason } });

// Employee Dashboard & Analytics
export const getEmployeeDashboard = (id) => api.get(`/employees/${id}/dashboard`);
export const getEmployeeTimesheets = (id, params) => api.get(`/employees/${id}/timesheets`, { params });

// Employee User Account Management
export const createEmployeeAccount = (id, role) => api.post(`/employees/${id}/create-account`, null, { params: { role } });
export const removeEmployeeAccount = (id) => api.delete(`/employees/${id}/account`);
export const resetEmployeePassword = (id) => api.post(`/employees/${id}/reset-password`);
export const updateEmployeeAccountStatus = (id, is_active) => api.put(`/employees/${id}/account-status`, null, { params: { is_active } });
export const updateEmployeeAccountRole = (id, role) => api.put(`/employees/${id}/account-role`, null, { params: { role } });

// Timesheet Approval Workflow
export const getPendingTimesheets = (params) => api.get('/timesheets/pending-approval', { params });
export const bulkApproveTimesheets = (entry_ids) => api.post('/timesheets/bulk-approve', { entry_ids });
export const bulkRejectTimesheets = (entry_ids, reason) => api.post('/timesheets/bulk-reject', { entry_ids, reason });

// Projects
export const getProjects = (params) => api.get('/projects', { params });
export const getProject = (id) => api.get(`/projects/${id}`);
export const getProjectDashboard = (id) => api.get(`/projects/${id}/dashboard`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);

// Tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const getTask = (id) => api.get(`/tasks/${id}`);
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);
export const takeTask = (id) => api.post(`/tasks/${id}/take`);
export const submitTask = (id) => api.post(`/tasks/${id}/submit`);
export const approveTask = (id) => api.post(`/tasks/${id}/approve`);
export const rejectTask = (id, reason) => api.post(`/tasks/${id}/reject`, null, { params: { reason } });

// Time Entries
export const getTimeEntries = (params) => api.get('/time-entries', { params });
export const createTimeEntry = (data) => api.post('/time-entries', data);
export const updateTimeEntry = (id, data) => api.put(`/time-entries/${id}`, data);
export const deleteTimeEntry = (id) => api.delete(`/time-entries/${id}`);
export const approveTimeEntry = (id) => api.post(`/time-entries/${id}/approve`);
export const rejectTimeEntry = (id, reason) => api.post(`/time-entries/${id}/reject`, null, { params: { reason } });

// Clients
export const getClients = (params) => api.get('/clients', { params });
export const getClient = (id) => api.get(`/clients/${id}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// Client 360 Dashboard
export const getClientDashboard = (id) => api.get(`/clients/${id}/dashboard`);
export const getClientProjects = (id, params) => api.get(`/clients/${id}/projects`, { params });
export const getClientProjectTasks = (clientId, projectId, params) => api.get(`/clients/${clientId}/projects/${projectId}/tasks`, { params });
export const getClientTeam = (id) => api.get(`/clients/${id}/team`);
export const getClientHours = (id, params) => api.get(`/clients/${id}/hours`, { params });
export const getClientFinancials = (id, params) => api.get(`/clients/${id}/financials`, { params });
export const getClientActivity = (id, params) => api.get(`/clients/${id}/activity`, { params });

// Invoices
export const getInvoices = (params) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const createInvoice = (data) => api.post('/invoices', data);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data);

// Payments
export const getPayments = (params) => api.get('/payments', { params });
export const createPayment = (data) => api.post('/payments', data);
export const approvePayment = (id) => api.post(`/payments/${id}/approve`);
export const markPaymentPaid = (id) => api.post(`/payments/${id}/mark-paid`);
export const deletePayment = (id) => api.post(`/payments/${id}/delete`);
export const calculatePayroll = (employeeId, periodStart, periodEnd) => 
  api.post('/payroll/calculate', null, { params: { employee_id: employeeId, period_start: periodStart, period_end: periodEnd } });
export const calculateDividendPayroll = (data) => api.post('/payments/dividend-payroll/calculate', data);

// Config
export const getComplexityPrices = () => api.get('/config/complexity-prices');
export const updateComplexityPrices = (prices) => api.put('/config/complexity-prices', prices);
export const getCompanyInfo = () => api.get('/company-info');

// Stakeholders
export const getStakeholders = () => api.get('/stakeholders');
export const getStakeholder = (id) => api.get(`/stakeholders/${id}`);
export const createStakeholder = (data) => api.post('/stakeholders', data);
export const updateStakeholder = (id, data) => api.put(`/stakeholders/${id}`, data);
export const deleteStakeholder = (id) => api.delete(`/stakeholders/${id}`);

// Quotations
export const getQuotations = (params) => api.get('/quotations', { params });
export const getQuotation = (id) => api.get(`/quotations/${id}`);
export const createQuotation = (data) => api.post('/quotations', data);
export const updateQuotation = (id, data) => api.put(`/quotations/${id}`, data);
export const convertQuotationToInvoice = (id) => api.post(`/quotations/${id}/convert-to-invoice`);

// Accounting
export const getAccountingEntries = (params) => api.get('/accounting', { params });
export const getAccountingSummary = (params) => api.get('/accounting/summary', { params });
export const getAccountingCategories = () => api.get('/accounting/categories');
export const createAccountingEntry = (data) => api.post('/accounting', data);
export const updateAccountingEntry = (id, data) => api.put(`/accounting/${id}`, data);
export const deleteAccountingEntry = (id) => api.delete(`/accounting/${id}`);

// Project Payments
export const getProjectPayments = (params) => api.get('/project-payments', { params });
export const createProjectPayment = (data) => api.post('/project-payments', data);
export const getProjectPaymentDistribution = (projectId) => api.get(`/project-payments/${projectId}/distribution`);

// Reports
export const getStakeholderComparison = (params) => api.get('/reports/stakeholder-comparison', { params });
export const getFinancialReport = (params) => api.get('/reports/financial', { params });
export const getProjectsProfitability = () => api.get('/reports/projects-profitability');
export const getEmployeeProductivity = (params) => api.get('/reports/employee-productivity', { params });

// Capacity Planning
export const getCapacityPlanning = (params) => api.get('/capacity-planning', { params });
export const getCapacityHeatmap = (params) => api.get('/capacity-planning/heatmap', { params });
export const getTimeAnalytics = (params) => api.get('/time-analytics', { params });

// Users Management
export const softDeleteUser = (id) => api.delete(`/users/${id}`);
export const restoreUser = (id) => api.post(`/users/${id}/restore`);
export const getRolesPermissions = () => api.get('/roles-permissions');

// Project Team & Capacity
export const getProjectTeam = (projectId) => api.get(`/projects/${projectId}/team`);
export const addToProjectTeam = (projectId, employeeId) => api.post(`/projects/${projectId}/team/${employeeId}`);
export const removeFromProjectTeam = (projectId, employeeId) => api.delete(`/projects/${projectId}/team/${employeeId}`);
export const getEmployeeCapacity = (employeeId) => api.get(`/employees/${employeeId}/capacity`);

// User Management
export const getUsers = (params) => api.get('/users', { params });
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const toggleUserStatus = (id) => api.put(`/users/${id}/toggle-status`);
export const adminResetPassword = (id, data) => api.put(`/users/${id}/reset-password`, data);

// Client Portal
export const getClientPortalDashboard = () => api.get('/client-portal/dashboard');

// Client Portal - Quotation Requests
export const createQuotationRequest = (data) => api.post('/client-portal/quotation-requests', data);
export const getMyQuotationRequests = (params) => api.get('/client-portal/quotation-requests', { params });
export const getMyQuotationRequest = (id) => api.get(`/client-portal/quotation-requests/${id}`);
export const addClientMessage = (id, message) => api.post(`/client-portal/quotation-requests/${id}/message`, null, { params: { message } });

// Admin - Quotation Requests Management
export const getQuotationRequests = (params) => api.get('/quotation-requests', { params });
export const getQuotationRequest = (id) => api.get(`/quotation-requests/${id}`);
export const updateQuotationRequest = (id, data) => api.put(`/quotation-requests/${id}`, data);
export const assignQuotationRequest = (id, employeeId) => api.post(`/quotation-requests/${id}/assign`, null, { params: { employee_id: employeeId } });
export const requestMoreInfo = (id, message) => api.post(`/quotation-requests/${id}/request-info`, null, { params: { message } });
export const approveQuotationRequest = (id) => api.post(`/quotation-requests/${id}/approve`);
export const rejectQuotationRequest = (id, reason) => api.post(`/quotation-requests/${id}/reject`, null, { params: { reason } });
export const convertToQuotation = (id) => api.post(`/quotation-requests/${id}/convert-to-quotation`);

// Bank Accounts
export const getBankAccounts = () => api.get('/bank-accounts');
export const createBankAccount = (data) => api.post('/bank-accounts', data);
export const updateBankAccount = (id, data) => api.put(`/bank-accounts/${id}`, data);
export const deleteBankAccount = (id) => api.delete(`/bank-accounts/${id}`);
export const getBankTransactions = (params) => api.get('/bank-transactions', { params });
export const createBankTransaction = (data) => api.post('/bank-transactions', data);

// Subscriptions
export const getSubscriptions = (params) => api.get('/subscriptions', { params });
export const getSubscription = (id) => api.get(`/subscriptions/${id}`);
export const createSubscription = (data) => api.post('/subscriptions', data);
export const updateSubscription = (id, data) => api.put(`/subscriptions/${id}`, data);
export const deleteSubscription = (id) => api.delete(`/subscriptions/${id}`);
export const generateSubscriptionQuotation = (id) => api.post(`/subscriptions/${id}/generate-quotation`);
export const registerSubscriptionPayment = (id, data) => api.post(`/subscriptions/${id}/payment`, data);
export const getSubscriptionReminders = (days) => api.get('/subscriptions/reminders/upcoming', { params: { days_ahead: days } });

// Billing Dashboard
export const getBillingMetrics = (params) => api.get('/billing/metrics', { params });

// GitHub Integration
export const getTaskGithub = (taskId) => api.get(`/tasks/${taskId}/github`);

// ============ NEW MODULES ============

// Notifications
export const getNotifications = (params) => api.get('/notifications', { params });
export const getUnreadCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');
export const deleteNotification = (id) => api.delete(`/notifications/${id}`);

// Notification Preferences (per-user)
export const getMyNotificationPreferences = () => api.get('/me/notification-preferences');
export const updateMyNotificationPreferences = (data) => api.put('/me/notification-preferences', data);

// Invoice Reminders (admin)
export const getInvoiceReminderSettings = () => api.get('/invoice-reminders/settings');
export const updateInvoiceReminderSettings = (data) => api.put('/invoice-reminders/settings', data);
export const runInvoiceReminders = (force = true) => api.post(`/invoice-reminders/run?force=${force}`);

// Audit
export const getAuditLogs = (params) => api.get('/audit/logs', { params });
export const getEntityAuditTrail = (entityType, entityId) => api.get(`/audit/logs/entity/${entityType}/${entityId}`);
export const getAuditSummary = (days) => api.get('/audit/logs/summary', { params: { days } });

// Vacations / Absences
export const getVacations = (params) => api.get('/vacations', { params });
export const createVacation = (data) => api.post('/vacations', data);
export const updateVacation = (id, data) => api.put(`/vacations/${id}`, data);
export const cancelVacation = (id) => api.delete(`/vacations/${id}`);
export const getVacationBalance = (employeeId) => api.get(`/vacations/balance/${employeeId}`);
export const getAbsenceCalendar = (params) => api.get('/vacations/calendar', { params });

// Suppliers
export const getSuppliers = (params) => api.get('/suppliers', { params });
export const getSupplier = (id) => api.get(`/suppliers/${id}`);
export const createSupplier = (data) => api.post('/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data);
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`);

// Purchase Orders
export const getPurchaseOrders = (params) => api.get('/purchase-orders', { params });
export const getPurchaseOrder = (id) => api.get(`/purchase-orders/${id}`);
export const createPurchaseOrder = (data) => api.post('/purchase-orders', data);
export const updatePurchaseOrder = (id, data) => api.put(`/purchase-orders/${id}`, data);
export const deletePurchaseOrder = (id) => api.delete(`/purchase-orders/${id}`);
export const uploadPurchaseOrderInvoice = (id, data) => api.post(`/purchase-orders/${id}/invoice-attachment`, data, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const downloadPurchaseOrderInvoice = (id) => api.get(`/purchase-orders/${id}/invoice-attachment/download`, {
  responseType: 'blob',
});

// Accounts Payable
export const getAccountsPayable = (params) => api.get('/accounts-payable', { params });

// ============ IT OPERATIONS ============

// IT Dashboard
export const getITDashboard = () => api.get('/it/dashboard');
export const getITAssetCategories = () => api.get('/it/asset-categories');

// Reference Data (visit types, priorities, etc. - dynamic from admin)
export const getITReferenceData = (category) => api.get('/it/reference-data', { params: { category } });
export const createITReferenceData = (data) => api.post('/it/reference-data', data);
export const updateITReferenceData = (id, data) => api.put(`/it/reference-data/${id}`, data);
export const deleteITReferenceData = (id) => api.delete(`/it/reference-data/${id}`);

// Client Locations
export const getITLocations = (params) => api.get('/it/locations', { params });
export const getITLocation = (id) => api.get(`/it/locations/${id}`);
export const createITLocation = (data) => api.post('/it/locations', data);
export const updateITLocation = (id, data) => api.put(`/it/locations/${id}`, data);
export const deleteITLocation = (id) => api.delete(`/it/locations/${id}`);

// IT Assets
export const getITAssets = (params) => api.get('/it/assets', { params });
export const getITAsset = (id) => api.get(`/it/assets/${id}`);
export const createITAsset = (data) => api.post('/it/assets', data);
export const updateITAsset = (id, data) => api.put(`/it/assets/${id}`, data);
export const deleteITAsset = (id) => api.delete(`/it/assets/${id}`);
export const getITAssetHistory = (id, params) => api.get(`/it/assets/${id}/history`, { params });

// Service Visits
export const getITVisits = (params) => api.get('/it/visits', { params });
export const getITVisit = (id) => api.get(`/it/visits/${id}`);
export const createITVisit = (data) => api.post('/it/visits', data);
export const updateITVisit = (id, data) => api.put(`/it/visits/${id}`, data);
export const updateITVisitStatus = (id, data) => api.put(`/it/visits/${id}/status`, data);
export const cancelITVisit = (id) => api.delete(`/it/visits/${id}`);

// Visit Assets
export const getVisitAssets = (visitId) => api.get(`/it/visits/${visitId}/assets`);
export const addVisitAsset = (visitId, data) => api.post(`/it/visits/${visitId}/assets`, data);
export const updateVisitAsset = (visitId, vaId, data) => api.put(`/it/visits/${visitId}/assets/${vaId}`, data);
export const removeVisitAsset = (visitId, vaId) => api.delete(`/it/visits/${visitId}/assets/${vaId}`);

// Visit Attachments
export const getVisitAttachments = (visitId) => api.get(`/it/visits/${visitId}/attachments`);
export const addVisitAttachment = (visitId, data) => api.post(`/it/visits/${visitId}/attachments`, data, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const deleteVisitAttachment = (visitId, attId) => api.delete(`/it/visits/${visitId}/attachments/${attId}`);
export const downloadVisitAttachment = (visitId, attId) => api.get(`/it/visits/${visitId}/attachments/${attId}/download`, { responseType: 'blob' });

// Generate Quote from Visit
export const generateQuoteFromVisit = (visitId, data) => api.post(`/it/visits/${visitId}/generate-quote`, data);

// Tickets / Mesa de Ayuda
export const getITTickets = (params) => api.get('/it/tickets', { params });
export const getITTicket = (id) => api.get(`/it/tickets/${id}`);
export const createITTicket = (data) => api.post('/it/tickets', data);
export const updateITTicket = (id, data) => api.put(`/it/tickets/${id}`, data);
export const addTicketComment = (id, data) => api.post(`/it/tickets/${id}/comments`, data);
export const generateVisitFromTicket = (id) => api.post(`/it/tickets/${id}/generate-visit`);

// Maintenance Plans
export const getITMaintenancePlans = (params) => api.get('/it/maintenance', { params });
export const getITMaintenancePlan = (id) => api.get(`/it/maintenance/${id}`);
export const createITMaintenancePlan = (data) => api.post('/it/maintenance', data);
export const updateITMaintenancePlan = (id, data) => api.put(`/it/maintenance/${id}`, data);
export const deleteITMaintenancePlan = (id) => api.delete(`/it/maintenance/${id}`);
export const executeMaintenancePlan = (id, data) => api.post(`/it/maintenance/${id}/execute`, data);

// Support Contracts / SLA
export const getITContracts = (params) => api.get('/it/contracts', { params });
export const getITContract = (id) => api.get(`/it/contracts/${id}`);
export const createITContract = (data) => api.post('/it/contracts', data);
export const updateITContract = (id, data) => api.put(`/it/contracts/${id}`, data);
export const consumeContractHours = (id, data) => api.post(`/it/contracts/${id}/consume-hours`, data);

// Spare Parts / Repuestos
export const getITSpareParts = (params) => api.get('/it/spare-parts', { params });
export const getITSparePart = (id) => api.get(`/it/spare-parts/${id}`);
export const createITSparePart = (data) => api.post('/it/spare-parts', data);
export const updateITSparePart = (id, data) => api.put(`/it/spare-parts/${id}`, data);
export const deleteITSparePart = (id) => api.delete(`/it/spare-parts/${id}`);
export const createInventoryMovement = (data) => api.post('/it/spare-parts/movement', data);

// Convert IT Quote to Invoice

// IT Cybersecurity Compliance (Resolución No. 07 - AIG)
// Checklist embebido en la visita; helpers dedicados para reportes y export.
export const saveVisitCompliance = (visitId, data) =>
  api.put(`/it/visits/${visitId}/cybersecurity-checklist`, data);
export const getITComplianceSummary = (params) =>
  api.get('/it/reports/compliance-summary', { params });
export const getITComplianceHistory = (clientId, params) =>
  api.get(`/it/reports/compliance-history/${clientId}`, { params });
export const getITComplianceAutogestion = (clientId, params) =>
  api.get(`/it/reports/compliance-autogestion/${clientId}`, { params });

// ============ SERVICE CATALOG ============
export const getServices = (params) => api.get('/services', { params });
export const getService = (id) => api.get(`/services/${id}`);
export const createService = (data) => api.post('/services', data);
export const updateService = (id, data) => api.put(`/services/${id}`, data);
export const deleteService = (id) => api.delete(`/services/${id}`);

export default api;
